import React, { useState, useEffect } from "react";
import API from "../../api";
import toastr from "toastr";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface TestType {
  id?: number;
  test_code: string;
  test_name: string;
  price: number | string;
  lower_limit?: string;
  upper_limit?: string;
  remarks?: string;
  method?: string;
  dept_id?: number;
  test_category?: number;
  interpretation?: string;
  type_test: string;
  table_input?: string;
  units?: string;
  critical_info?: string;
  comments?: string;
  sub_head?: string;
  sample_type?: string;
  show_critical_info?: number; 
  show_interpretation?: number;
}

interface TestCategory {
  id: number;
  category_name: string;
  dept_id: number;
}

interface Props {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  testTypeData: TestType | null;
  onSuccess: () => void;
  existingTests?: TestType[];
}

// Utility: Strip HTML tags for plain text display (if needed elsewhere)
export const stripHtml = (html: string | undefined): string => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const TestTypeModal: React.FC<Props> = ({
  show,
  onClose,
  mode,
  testTypeData,
  onSuccess,
  existingTests = [],
}) => {
  const [testCode, setTestCode] = useState("");
  const [testName, setTestName] = useState("");
  const [price, setPrice] = useState("");
  const [lowerLimit, setLowerLimit] = useState("");
  const [upperLimit, setUpperLimit] = useState("");
  const [remarks, setRemarks] = useState("");
  const [method, setMethod] = useState("");
  const [testCategory, setTestCategory] = useState<number>(0);
  const [interpretation, setInterpretation] = useState("");
  const [typeTest, setTypeTest] = useState("Normal");
  const [tableInput, setTableInput] = useState("");
  const [units, setUnits] = useState("");
  const [criticalInfo, setCriticalInfo] = useState("");
  const [comments, setComments] = useState("");
  const [subHead, setSubHead] = useState("");
  const [sampleType, setSampleType] = useState("");
  const [showCriticalInfo, setShowCriticalInfo] = useState(false);
  const [showInterpretation, setShowInterpretation] = useState(false);
  const [categories, setCategories] = useState<TestCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!show) return;
    if (mode === "edit" && testTypeData) {
      setTestCode(testTypeData.test_code || "");
      setTestName(testTypeData.test_name || "");
      setPrice(String(testTypeData.price || ""));
      setLowerLimit(testTypeData.lower_limit || "");
      setUpperLimit(testTypeData.upper_limit || "");
      setRemarks(testTypeData.remarks || "");
      setMethod(testTypeData.method || "");
      setTestCategory(testTypeData.test_category || 0);
      setInterpretation(testTypeData.interpretation || "");
      setTypeTest(testTypeData.type_test || "Normal");
      setTableInput(testTypeData.table_input || "");
      setUnits(testTypeData.units || "");
      setCriticalInfo(testTypeData.critical_info || "");
      setComments(testTypeData.comments || "");
      setSubHead(testTypeData.sub_head || "");
      setSampleType(testTypeData.sample_type || "");
      setShowCriticalInfo(!!testTypeData.show_critical_info);
      setShowInterpretation(!!testTypeData.show_interpretation);
    } else if (mode === "add") {
      resetForm();
      fetchNextTestCode();
    }
  }, [mode, testTypeData, show]);

  const fetchCategories = async () => {
    try {
      const res = await API.get("testCategories/getCategories");
      setCategories(res.data.data || []);
    } catch {
      toastr.error("Failed to fetch categories");
    }
  };

  const generateCodeFromExisting = (): string => {
    let maxNum = 0;
    existingTests.forEach((t) => {
      const match = t.test_code?.match(/L-T(\d+)/i);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    });
    const next = maxNum + 1;
    return `L-T${String(next).padStart(4, "0")}`;
  };

  const fetchNextTestCode = async () => {
    setCodeLoading(true);
    try {
      const res = await API.get("testTypes/getNextTestCode");
      const nextCode =
        res.data?.next_code ||
        res.data?.test_code ||
        res.data?.data?.next_code ||
        res.data?.data?.test_code ||
        "";
      setTestCode(nextCode ? String(nextCode) : generateCodeFromExisting());
    } catch {
      setTestCode(generateCodeFromExisting());
    } finally {
      setCodeLoading(false);
    }
  };

  const resetForm = () => {
    setTestCode("");
    setTestName("");
    setPrice("");
    setLowerLimit("");
    setUpperLimit("");
    setRemarks("");
    setMethod("");
    setTestCategory(0);
    setInterpretation("");
    setTypeTest("Normal");
    setTableInput("");
    setUnits("");
    setCriticalInfo("");
    setComments("");
    setSubHead("");
    setSampleType("");
    setShowCriticalInfo(false);
    setShowInterpretation(false);
  };

  const handlePriceInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*\.?\d*$/.test(val)) setPrice(val);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testName.trim()) { toastr.error("Test name is required"); return; }
    if (!testCategory) { toastr.error("Test category is required"); return; }
    if (!price) { toastr.error("Price is required"); return; }

    setLoading(true);
    try {
      const payload = {
        test_code: testCode.trim() || null,
        test_name: testName.trim(),
        price: parseFloat(price),
        lower_limit: lowerLimit.trim() || null,
        upper_limit: upperLimit.trim() || null,
        remarks: remarks.trim() || null,
        method: method.trim() || null,
        test_category: testCategory,
        interpretation: interpretation.trim() || null,
        type_test: typeTest,
        table_input: tableInput.trim() || null,
        units: units.trim() || null,
        critical_info: criticalInfo.trim() || null,
        comments: comments.trim() || null,
        sub_head: subHead.trim() || null,
        sample_type: sampleType.trim() || null,
        show_critical_info: showCriticalInfo ? 1 : 0,
        show_interpretation: showInterpretation ? 1 : 0,
      };

      if (mode === "add") {
        await API.post("testTypes/createTestType", payload);
        toastr.success("Test created successfully");
      } else {
        await API.put(`testTypes/updateTestType/${testTypeData?.id}`, payload);
        toastr.success("Test updated successfully");
      }
      onSuccess();
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const isTableType = typeTest === "Table";

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "align",
    "link",
    "image",
  ];

  const quillStyle = {
    backgroundColor: "#1e2130",
    color: "#ffffff",
    border: "1px solid #3a3f55",
    borderRadius: "4px",
  };

  if (!show) return null;

  const inputCls = "form-control form-control-sm bg-dark text-white border-secondary";
  const selectCls = "form-select form-select-sm bg-dark text-white border-secondary";
  const labelCls = "form-label text-white-50 small mb-1";

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.7)",
          zIndex: 1040,
        }}
        onClick={onClose}
      />

      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1050,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: "20px 12px",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "860px",
            background: "#1e2130",
            borderRadius: "10px",
            border: "1px solid #3a3f55",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: "16px 20px",
              borderBottom: "1px solid #3a3f55",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#161827",
              borderRadius: "10px 10px 0 0",
            }}
          >
            <h5 className="mb-0 text-white fw-semibold">
              <i
                className={`fa fa-${
                  mode === "add" ? "plus-circle" : "edit"
                } me-2 text-primary`}
              />
              {mode === "add" ? "Create New Test" : "Update Test Details"}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              style={{ opacity: 0.7 }}
            />
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div style={{ padding: "20px", overflowY: "auto" }}>

              {/* ── Basic Information ── */}
              <div className="mb-3">
                <p
                  className="text-uppercase small fw-bold mb-2"
                  style={{ color: "#6c7293", letterSpacing: "0.08em" }}
                >
                  Basic Information
                </p>
                <div className="row g-3">

                  <div className="col-md-4">
                    <label className={labelCls}>Test Code</label>
                    <div className="position-relative">
                      <input
                        type="text"
                        className={inputCls}
                        value={testCode}
                        onChange={(e) => {
                          if (mode === "edit") setTestCode(e.target.value);
                        }}
                        placeholder={codeLoading ? "Generating..." : ""}
                        readOnly={mode === "add"}
                        style={{
                          cursor: mode === "add" ? "default" : undefined,
                          opacity: codeLoading ? 0.5 : 1,
                          background: mode === "add" ? "#1a2a2a" : undefined,
                          color: mode === "add" ? "#4ecca3" : undefined,
                          fontWeight: mode === "add" ? 600 : undefined,
                        }}
                      />
                      {codeLoading && (
                        <span
                          className="spinner-border spinner-border-sm text-secondary"
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        />
                      )}
                    </div>
                    {mode === "add" && !codeLoading && testCode && (
                      <small className="text-success mt-1 d-block">
                        <i className="fa fa-check-circle me-1" />
                        Auto-generated
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className={labelCls}>
                      Test Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={inputCls}
                      value={testName}
                      onChange={(e) => setTestName(e.target.value)}
                      placeholder="Enter test name"
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className={labelCls}>
                      Test Department <span className="text-danger">*</span>
                    </label>
                    <select
                      className={selectCls}
                      value={testCategory}
                      onChange={(e) => setTestCategory(Number(e.target.value))}
                      required
                    >
                      <option value={0}>Select Department</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className={labelCls}>
                      Price <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={inputCls}
                      value={price}
                      onChange={handlePriceInput}
                      placeholder="Enter price"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className={labelCls}>Method</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                      placeholder="Enter method"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className={labelCls}>Sample Type</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={sampleType}
                      onChange={(e) => setSampleType(e.target.value)}
                      placeholder="Enter sample type"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className={labelCls}>Formula / Remarks</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Enter formula/remarks"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className={labelCls}>Units</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={units}
                      onChange={(e) => setUnits(e.target.value)}
                      placeholder="Enter units"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className={labelCls}>Type of Test</label>
                    <select
                      className={selectCls}
                      value={typeTest}
                      onChange={(e) => setTypeTest(e.target.value)}
                    >
                      <option value="Normal">Normal</option>
                      <option value="Sub Heading">Sub Heading</option>
                      <option value="Paragraph">Paragraph</option>
                      <option value="Table">Table</option>
                      <option value="Date">Date</option>
                      <option value="Time">Time</option>
                      <option value="Image">Image</option>
                    </select>
                  </div>
                </div>
              </div>

              <hr style={{ borderColor: "#3a3f55", margin: "8px 0 16px" }} />

              {/* ── Reference Range / Table Config ── */}
              <div className="mb-3">
                <p
                  className="text-uppercase small fw-bold mb-2"
                  style={{ color: "#6c7293", letterSpacing: "0.08em" }}
                >
                  {isTableType ? "Table Configuration" : "Reference Range"}
                </p>
                {isTableType ? (
                  <div className="row g-3">
                    <div className="col-12">
                      <label className={labelCls}>Table Input</label>
                      <textarea
                        className={inputCls}
                        rows={3}
                        value={tableInput}
                        onChange={(e) => setTableInput(e.target.value)}
                        placeholder="Enter table input"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className={labelCls}>Lower Limit</label>
                      <input
                        type="text"
                        className={inputCls}
                        value={lowerLimit}
                        onChange={(e) => setLowerLimit(e.target.value)}
                        placeholder="Enter lower limit"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className={labelCls}>Upper Limit</label>
                      <input
                        type="text"
                        className={inputCls}
                        value={upperLimit}
                        onChange={(e) => setUpperLimit(e.target.value)}
                        placeholder="Enter upper limit"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className={labelCls}>Sub Heading Name</label>
                      <input
                        type="text"
                        className={inputCls}
                        value={subHead}
                        onChange={(e) => setSubHead(e.target.value)}
                        placeholder="Enter sub heading"
                      />
                    </div>
                  </div>
                )}
              </div>

              <hr style={{ borderColor: "#3a3f55", margin: "8px 0 16px" }} />

              {/* ── Critical Info ── */}
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <p
                    className="text-uppercase small fw-bold mb-0"
                    style={{ color: "#6c7293", letterSpacing: "0.08em" }}
                  >
                    Critical Info
                  </p>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showCriticalInfo"
                      checked={showCriticalInfo}
                      onChange={(e) => setShowCriticalInfo(e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                    <label
                      className="form-check-label text-white-50 small"
                      htmlFor="showCriticalInfo"
                    >
                      Show on Report
                    </label>
                  </div>
                </div>
                <ReactQuill
                  theme="snow"
                  value={criticalInfo}
                  onChange={setCriticalInfo}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter critical info..."
                  style={quillStyle}
                />
              </div>

              <hr style={{ borderColor: "#3a3f55", margin: "8px 0 16px" }} />

              {/* ── Interpretation ── */}
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <p
                    className="text-uppercase small fw-bold mb-0"
                    style={{ color: "#6c7293", letterSpacing: "0.08em" }}
                  >
                    Interpretation
                  </p>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showInterpretation"
                      checked={showInterpretation}
                      onChange={(e) => setShowInterpretation(e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                    <label
                      className="form-check-label text-white-50 small"
                      htmlFor="showInterpretation"
                    >
                      Show on Report
                    </label>
                  </div>
                </div>
                <ReactQuill
                  theme="snow"
                  value={interpretation}
                  onChange={setInterpretation}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter interpretation..."
                  style={quillStyle}
                />
              </div>

              <hr style={{ borderColor: "#3a3f55", margin: "8px 0 16px" }} />

              {/* ── Comments ── */}
              <div className="mb-1">
                <p
                  className="text-uppercase small fw-bold mb-2"
                  style={{ color: "#6c7293", letterSpacing: "0.08em" }}
                >
                  Comments
                </p>
                <ReactQuill
                  theme="snow"
                  value={comments}
                  onChange={setComments}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter comments..."
                  style={quillStyle}
                />
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "14px 20px",
                borderTop: "1px solid #3a3f55",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                background: "#161827",
                borderRadius: "0 0 10px 10px",
              }}
            >
              <button
                type="button"
                className="btn btn-sm btn-secondary px-4"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-sm btn-primary px-4"
                disabled={loading || codeLoading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                    />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save me-1" />
                    {mode === "add" ? "Create Test" : "Update Test"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default TestTypeModal;